<script>
    /**
     * SPDX-FileCopyrightText: 2012-2026 Open Assessment Technologies S.A.
     * Copyright (C) 2026 (original work) Open Assessment Technologies S.A.
     *
     * SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-TAO-Commercial-License
     */
    import { getActualKey, getPointerEventCoords } from '@oat-sa-private/ui-core';
    import Prompt from '../../Prompt.svelte';
    import Svg from '../Svg.svelte';
    import ChoiceFeedbackBlock from '../../feedback/ChoiceFeedbackBlock.svelte';
    import AtomicAriaLive from '../../AtomicAriaLive.svelte';
    import { getInteractionStateStore } from '../../../itemsStateStore.js';
    import { getItemSessionStatusStore } from '../../../itemsSessionStatusStore.js';
    import itemSessionStatus from '../../../itemSessionStatus.js';
    import { calculateScalingFactor, getScaledCoords, getUsableHeight } from '../util/scaling.js';
    import { isRTLElement, sortChoicesByBoundingBox } from '../util/focusorder.js';
    import ariaHelperFactory from './util/ariaHelper.js';
    import forwardFocusToChoice from '../util/actions/forwardFocusToChoice.js';
    import arrowKeysFocusLoop from '../util/actions/arrowKeysFocusLoop.js';
    import preventSpaceScroll from '../../util/actions/preventSpaceScroll.js';
    import resizeObserve from '../../util/actions/resizeObserve.js';
    import AssociationLine from './AssociationLine.svelte';
    import HotspotChoice from '../HotspotChoice.svelte';
    import { handlersFactory } from './util/handler.js';
    import { DeferredPromise } from '../../util/promise.js';
    import { getContext, untrack } from 'svelte';
    import { resolveImage } from '../util/resolveImage.js';

    const qtiClass = 'qti-graphicAssociateInteraction';

    let {
        // keys for state store:
        itemIdentifier,
        responseIdentifier,

        // inherited item-level QTI attributes:
        language,
        id,
        dir,

        // inherited aria attributes:
        role,
        ariaAttrs = {},
        dataAttrs = {},

        // interaction-level QTI attributes:
        minAssociations = 0,
        maxAssociations = 1,
        prompt,

        classes = '',
        disabled = false,

        // response format
        cardinality,

        /**
         * @typedef HotspotChoice - mapped from QTI HotspotChoice
         * @property {String} key
         * @property {String} shape
         * @property {String} coords
         * @property {Number} matchMin
         * @property {Number} matchMax
         * @property {Boolean} fixed
         * @property {String} hotspotLabel - for SR
         * @property {Object} [svg] - not passed, but set by interaction
         */
        /**
         * @type {HotspotChoice[]} - from itemData choices
         */
        choices = [],

        /**
         * @typedef ImgObject - background image of the graphic interaction
         * @property {String} data - equivalent to src
         * @property {Number} width
         * @property {Number} height
         */
        /**
         * @type {ImgObject}
         */
        imgObject
    } = $props();

    // svelte-ignore state_referenced_locally
    const itemContext = getContext(itemIdentifier);
    const instructionsLang = itemContext && itemContext.getInstructionsLang();

    // svelte-ignore state_referenced_locally
    const interactionStateStore = getInteractionStateStore(itemIdentifier, responseIdentifier),
        itemSessionStatusStore = getItemSessionStatusStore(itemIdentifier);

    // svelte-ignore non_reactive_update
    let qtiMinAssociationsMessage, qtiMaxAssociationsMessage, useArrow, baseType, imgSrc;

    setDerivedProps();

    function setDerivedProps() {
        if (!cardinality) {
            cardinality = maxAssociations === 1 ? 'single' : 'multiple';
        }

        qtiMinAssociationsMessage = dataAttrs['data-min-associations-message'];
        qtiMaxAssociationsMessage = dataAttrs['data-max-associations-message'];
        useArrow = dataAttrs['data-interaction-subtype'] === 'arrow';
        baseType = useArrow ? 'directedPair' : 'pair';

        imgSrc = resolveImage(itemContext, imgObject.data);
    }

    // shape center - start or end points of line
    // svelte-ignore state_referenced_locally
    let shapeCenter = $state(
        choices.reduce(function (result, item) {
            result[item.key] = [];
            return result;
        }, {})
    );

    function isTruthyDataAttr(value) {
        return value === true || value === 'true' || value === 1 || value === '1';
    }

    function isArrowStartChoice(choiceKey) {
        if (!useArrow) {
            return true;
        }
        const choice = getChoiceById(choiceKey);
        return Boolean(choice && isTruthyDataAttr(choice['data-start']));
    }

    function isArrowEndChoice(choiceKey) {
        if (!useArrow) {
            return true;
        }
        const choice = getChoiceById(choiceKey);
        return Boolean(choice && isTruthyDataAttr(choice['data-end']));
    }

    function getArrowDirection(pair) {
        const [firstKey, secondKey] = pair;
        if (isArrowStartChoice(firstKey) && isArrowEndChoice(secondKey)) {
            return { startKey: firstKey, endKey: secondKey };
        }
        if (isArrowStartChoice(secondKey) && isArrowEndChoice(firstKey)) {
            return { startKey: secondKey, endKey: firstKey };
        }
        return { startKey: firstKey, endKey: secondKey };
    }

    function getArrowPoints(pair) {
        if (!useArrow) {
            return null;
        }
        const { startKey, endKey } = getArrowDirection(pair);
        const startPoint = shapeCenter[startKey];
        const endPoint = shapeCenter[endKey];
        if (!startPoint || !endPoint || !startPoint.length || !endPoint.length) {
            return null;
        }
        return { startPoint, endPoint };
    }

    function getArrowStartPoint(pair) {
        const points = getArrowPoints(pair);
        return points ? points.startPoint : null;
    }

    function getArrowEndPoint(pair) {
        const points = getArrowPoints(pair);
        return points ? points.endPoint : null;
    }

    /**
     * Collect hotspots pair associated by line in selected state
     * @type {String[]}
     */
    let selectedPair = $state(null);

    let pairs = $state([]);

    /**
     * @type {Object[]} pairElements - svg elements of association lines
     */
    let pairElements = [];
    /**
     * @type {String[]} orderedElementsKeys - key of all elements which can be focused (choices and association lines)
     */
    let orderedElementsKeys = $state();
    //selected  choice details. Opposite to 'targeted' - never should be set if 'targeted' is set
    let selectedChoice = $state();
    // choice which is start point of line drawing. Opposite to 'selectedChoice' - never should be set if 'selectedChoice' is set
    let targeted = $state();

    /**
     * @type {String[]} a new list of the choiceKeys, ordered into tabbing order
     * Initially undefined, but set once only, after all the bounding boxes are known
     */
    let orderedChoicesKeys = $state();

    let hasFocus = $state(false);
    let lastFocusedChoiceKey = $state();
    let focusShadowLine = false; // allow focusing 'remove button' asynchronously

    // drawing line by mouse dragging
    let dragging = $state(false); // start drag mouse(we do not use real drag, however user behavior the same).
    let start = $state(); // coordinates of start point while drawing line
    let end = $state(); // coordinates of end point while drawing line
    let isLineDrawing = $state(false); // start drawing line

    let svgElement;
    let interactionElement = $state();
    let isRTL = $derived(interactionElement && isRTLElement(interactionElement));

    let windowHeight = $state();
    let containerWidth = $state();
    let svgLeftCoord = 0; //left coordinate of top left corner of Svg component
    let svgTopCoord = 0; //top coordinate of top left corner of Svg component

    /**
     * Calculate the SVG scaling factor based on document dimensions, and set scaled image dimensions.
     * Scaling factor which should be applied to all SVG children
     */
    let scalingFactor = $derived.by(() => {
        if (containerWidth && windowHeight) {
            const usableHeight = getUsableHeight(windowHeight);
            return calculateScalingFactor(imgObject.width, imgObject.height, containerWidth, usableHeight);
        } else {
            return 1;
        }
    });
    let imgWidth = $derived(scalingFactor * imgObject.width);
    let imgHeight = $derived(scalingFactor * imgObject.height);

    let isInteractionFocused = $state(false); // becomes true when interaction is focused for the first time

    // aria
    const ariaHelper = ariaHelperFactory();
    let announcement = $state();

    //image loading
    const imageLoadingPromise = new DeferredPromise();
    itemContext.registerLoadingElement(imageLoadingPromise.promise);

    let disabledBySession = $derived($itemSessionStatusStore === itemSessionStatus.closed);
    let handler = $derived(handlersFactory(disabled || disabledBySession));

    // do initial response definition
    if (!interactionStateStore.hasResponse()) {
        interactionStateStore.merge({ qtiClass });
        storeResponse();
    }

    loadResponse();
    $effect(() => {
        if ($interactionStateStore) {
            untrack(() => {
                loadResponse();
            });
        }
    });

    /**
     * Validate pairs
     * @returns {Boolean} - validity
     */
    function getPairsValidity() {
        let validity = true;

        const associations = pairs.filter(pair => pair[0] && pair[1]);

        // validate association count
        if (
            (minAssociations > 0 && associations.length < minAssociations) ||
            (maxAssociations > 0 && associations.length > maxAssociations)
        ) {
            validity = false;
        }

        // validate matchMin and matchMax properties of choices
        if (validity) {
            choices.some(choice => {
                const choiceUsageCount = getChoiceUsageCountInCompletedPairs(choice.key);
                if (
                    (choice.matchMin !== 0 && choiceUsageCount < choice.matchMin) ||
                    (choice.matchMax !== 0 && choiceUsageCount > choice.matchMax)
                ) {
                    validity = false;
                }
                return !validity;
            });
        }

        return validity;
    }

    /**
     * Load the interaction response
     */
    function loadResponse() {
        let storedResponse = interactionStateStore.getResponseValue();

        if (!storedResponse) {
            storedResponse = [];
        } else if (cardinality === 'single') {
            storedResponse = [storedResponse];
        }

        pairs = storedResponse;

        const state = interactionStateStore.get();
        if (state.choiceKeys) {
            choices = state.choiceKeys.map(choiceKey => choices.find(choice => choice.key === choiceKey));
        }
    }

    /**
     * Format and store selected value in interactionStateStore
     */
    function storeResponse() {
        const choiceKeys = choices.map(choice => choice.key);
        interactionStateStore.update({ pairs: $state.snapshot(pairs), choiceKeys });
        let responseValue = null;
        if (cardinality === 'single') {
            if (pairs && pairs[0] && pairs[0][0] && pairs[0][1]) {
                responseValue = pairs[0];
            }
        } else {
            //store only pairs that have both identifiers set
            responseValue = pairs.filter(pair => pair.length === 2 && pair[0] && pair[1]);
        }
        interactionStateStore.setResponseValue(
            {
                cardinality,
                baseType,
                value: $state.snapshot(responseValue)
            },
            getPairsValidity()
        );
    }

    /**
     * Focuses a choice and sets it as the last focused choice
     * Also arranges it to the front
     * @param {String} choiceKey
     * @returns {Boolean} true if focused
     */
    function focusChoiceWithKey(choiceKey) {
        let focusedElement = choices.find(choice => choice.key === choiceKey);
        //focus hotspots
        if (focusedElement && focusedElement.svg) {
            const parent = focusedElement.svg.parent('.hotspot-choice');
            if (parent) {
                //change order in case of intersections hotspots
                parent.front();
            }
            selectedPair = null; // if we focusing hotspots we have to unselect all lines
            focusedElement.svg.node.focus();
            return true;
        }
        // focus lines
        if (!focusedElement) {
            focusedElement = pairElements.find(link => link.key === choiceKey);
            if (focusedElement) {
                selectedPair = [focusedElement.pair[0], focusedElement.pair[1]];
                clearSelectedChoice();
                focusShadowLine = true;
                return true;
            }
        }
        return false;
    }

    /**
     * Actualize 'orderedElementsKeys' variable in case of adding/removing pairs
     */
    function updateOrderedKeysList() {
        if (!orderedChoicesKeys) {
            return;
        }
        const orderedAssociations = sortChoicesByBoundingBox(pairElements, isRTL);
        orderedElementsKeys = [...orderedChoicesKeys, ...orderedAssociations];
    }

    /**
     * Focus remove button after association line fully rendered
     * @param {Object} e
     */
    function handleDrawShadowLine(e) {
        if (focusShadowLine) {
            const lineElement = e.svgGroup;
            lineElement.node.querySelector('.button-container').focus();
            focusShadowLine = false;
        }
    }

    /**
     * Test that user tap on html element, but not drag
     * @param {HTMLElement} touchStartEl - element for touchstart event
     * @param {HTMLElement} touchEndEl - element for touchend event
     * @returns {Boolean} true if user tap on element
     */
    function isTap(touchStartEl, touchEndEl) {
        return touchStartEl.isSameNode(touchEndEl);
    }

    /**
     * Calculate next element in an array of keys by cycle
     * @param {String} currentChoiceKey key of current choice
     * @param {Array} elements - array of keys
     * @returns {String} next key
     */
    function getNextOrderedKey(currentChoiceKey, elements) {
        if (!elements.length) {
            return currentChoiceKey;
        }
        const currentIndex = elements.indexOf(currentChoiceKey);
        const nextIndex = (currentIndex + 1) % elements.length;
        return elements[nextIndex];
    }

    /**
     * Gets aria label for a choice depending on cardinality
     * @param {Object} choice
     * @param {String[]} [orderedChoiceKeys=orderedChoicesKeys] Ordered choice keys used to build positional aria labels.
     * @param {Object|null} [currentSelectedChoice=selectedChoice] Currently selected choice used to compute selection-specific hints.
     * @returns {String}
     */
    function getChoiceAriaLabel(
        choice,
        orderedChoiceKeys = orderedChoicesKeys,
        currentSelectedChoice = selectedChoice
    ) {
        return cardinality === 'single'
            ? getChoiceSingleAriaLabel(choice, orderedChoiceKeys)
            : getChoiceMultiAriaLabel(choice, orderedChoiceKeys, currentSelectedChoice);
    }

    /**
     * Gets from a helper the full aria label for a choice with cardinality 'multiple'
     * @param {Object} choice
     * @param {String[]} [orderedChoiceKeys=orderedChoicesKeys] Ordered choice keys used to build positional aria labels.
     * @param {Object|null} [currentSelectedChoice=selectedChoice] Currently selected choice used to compute association-creation hints.
     * @returns {String}
     */
    function getChoiceMultiAriaLabel(
        choice,
        orderedChoiceKeys = orderedChoicesKeys,
        currentSelectedChoice = selectedChoice
    ) {
        let label = '';
        let pairedKeys = [];
        pairs.forEach(pair => {
            if (pair[0] === choice.key) {
                pairedKeys.push(getChoiceById(pair[1]));
            }
            if (pair[1] === choice.key) {
                pairedKeys.push(getChoiceById(pair[0]));
            }
        });
        if (choiceValidByMatchMax(choice)) {
            let describedBy = ariaHelper.getChoiceDescribedBy();
            if (currentSelectedChoice && currentSelectedChoice.key === choice.key) {
                describedBy = ariaHelper.getSelectedChoiceDescribedBy();
            }
            if (currentSelectedChoice && currentSelectedChoice.key !== choice.key) {
                describedBy = ariaHelper.getAssociationCreationDescribedBy();
            }
            if (isChoiceDisabled(choice)) {
                describedBy = ariaHelper.getDisabledChoiceDescribedBy();
            }
            label = [ariaHelper.getChoiceAriaLabel(choice, pairedKeys, orderedChoiceKeys), describedBy].join(' ');
        } else {
            let describedBy = ariaHelper.getFulfilledChoiceDescribedBy();
            if (isChoiceInactive(choice)) {
                describedBy = ariaHelper.getInactiveChoiceDescribedBy();
            }
            label = [ariaHelper.getChoiceMaxAssociationLabel(choice, pairedKeys, orderedChoiceKeys), describedBy].join(
                ' '
            );
        }

        return label;
    }

    /**
     * Gets from a helper the full aria label for a choice with cardinality 'single'
     * @param {Object} choice
     * @param {String[]} [orderedChoiceKeys=orderedChoicesKeys] Ordered choice keys used to build positional aria labels.
     * @returns {String}
     */
    function getChoiceSingleAriaLabel(choice, orderedChoiceKeys = orderedChoicesKeys) {
        let pairedKeys = [];
        let describedBy = ariaHelper.getChoiceDescribedBy();
        pairs.forEach(pair => {
            if (pair[0] === choice.key) {
                pairedKeys.push(getChoiceById(pair[1]));
            }
            if (pair[1] === choice.key) {
                pairedKeys.push(getChoiceById(pair[0]));
            }
        });
        if (pairs.length === 1 && isChoiceDisabled(choice)) {
            describedBy = ariaHelper.getDisabledChoiceDescribedBy();
        }
        if (pairs.length === 1 && isChoiceInactive(choice)) {
            describedBy = ariaHelper.getInactiveChoiceDescribedBy();
        }

        return [ariaHelper.getChoiceAriaLabel(choice, pairedKeys, orderedChoiceKeys), describedBy].join(' ');
    }

    /**
     * Gets label for remove button in association line
     * @param {Array} pair
     * @returns {String}
     */
    function getRemoveButtonAriaLabel(pair) {
        return ariaHelper.getRemoveButtonAriaLabel(getChoiceById(pair[0]), getChoiceById(pair[1]), orderedChoicesKeys);
    }

    /***** pairs helpers ****/

    /**
     * Generate pair key
     * @param {Array} pair
     * @returns {String}
     */
    function getPairKey(pair) {
        return `${pair[0]}_${pair[1]}`;
    }

    /**
     * Test that choice key belongs to any pair
     * @param {String} key - id of choice to test
     * @returns {boolean}
     */
    function choiceInPair(key) {
        return pairs.some(pair => pair[0] === key || pair[1] === key);
    }

    /**
     * Test that pairs consists of two keys
     * @param {Array} pair - existing pair of choices
     * @param {String} firstKey - id of choice in a pair
     * @param {String} secondKey - id of choice in a pair
     * @returns {Boolean}
     */
    function isPair(pair, firstKey, secondKey) {
        return (pair[0] === firstKey && pair[1] === secondKey) || (pair[1] === firstKey && pair[0] === secondKey);
    }

    /**
     * Test that association between two choices exists
     * @param {String} firstKey - id of choice in a pair
     * @param {String} secondKey - id of choice in a pair
     * @returns {Boolean} exists pair or not
     */
    function isPairExists(firstKey, secondKey) {
        return Boolean(pairs.filter(pair => isPair(pair, firstKey, secondKey)).length);
    }

    /**
     * Remove a pair
     * @param {Array} pair
     */
    function removePair(pair) {
        pairs = pairs.filter(item => !(pair[0] === item[0] && pair[1] === item[1]));
        storeResponse();
        pairElements = pairElements.filter(link => link.key !== getPairKey(pair));
        selectedPair = null; // unselect association
        updateOrderedKeysList(); //recalculate list of focusable elements
        announcement = ariaHelper.announceRemoved(getChoiceById(pair[0]), getChoiceById(pair[1]), orderedChoicesKeys);
    }

    /**
     * Add item in new pair
     * @param {string} firstKey id of choice
     * @param {string} secondKey id of choice
     */
    function addPair(firstKey, secondKey) {
        pairs.push([firstKey, secondKey]);
        storeResponse();
        announcement = ariaHelper.announceAdded(getChoiceById(firstKey), getChoiceById(secondKey), orderedChoicesKeys);
    }

    /**** choice helpers *****/

    /**
     * Get array of choices objects
     * @param {Array} selectedKeys - array of choice keys related with selected line
     * @returns {Array}
     */
    function getSelectedChoices(selectedKeys) {
        return selectedKeys.map(key => getChoiceById(key));
    }

    /**
     * Count choice usage in *completed* pairs (for validity)
     * @param {string} choiceId
     * @returns {number}
     */
    function getChoiceUsageCountInCompletedPairs(choiceId) {
        return pairs
            .filter(pair => pair[0] && pair[1])
            .reduce((total, pair) => total + (pair[0] === choiceId) + (pair[1] === choiceId), 0);
    }

    /**
     * @param {String} choiceId
     * @returns {Array|null} choice details, or null if choice not found
     */
    function getChoiceById(choiceId) {
        if (!choiceId) {
            return null;
        }
        return choices.find(choice => choice.key === choiceId) || null;
    }

    /**
     * Set selected choice
     * @param {String} key choice key
     */
    function setSelectedChoice(key) {
        selectedChoice = choices.filter(choice => choice.key === key).pop();
    }

    /**
     * Unset selected choice
     */
    function clearSelectedChoice() {
        selectedChoice = null;
    }

    /**
     * Test that current choice is a start point of drawing line
     * @param {String} key - choice key
     * @returns {Boolean} choice is a starting hotspot for drawing line
     */
    function choiceIsStartPointOfDrawingLine(key) {
        return targeted && targeted.key === key;
    }

    /**
     * Test that association is permitted for the choice
     * @param {String} choiceKey - key for choice which is target for finalizing current action (e.g. second choice for drawing line)
     * @returns {Boolean} can hotspot be a target of association creation
     */
    function associationAllowed(choiceKey) {
        // check that first and second choices of association are the same choice (click/tap)
        const theSameChoice = selectedChoice && selectedChoice.key === choiceKey;

        // only end choice can be a targeted while drawing line (drawing)
        const isStartPoint = choiceIsStartPointOfDrawingLine(choiceKey);

        //first and second choices shouldn't be already paired
        const isChoicePairedWithSelected = selectedChoice && isPairExists(selectedChoice.key, choiceKey);

        // check that choice allowed for targeting according to matchMin and matchMax limitations
        let isPermitted = true;
        //start hotspot in case of 'click-click' interacting
        if (selectedChoice) {
            isPermitted = choiceValidByMatchMax(selectedChoice);
        }
        //start hotspot in case of 'draw start - draw end' interacting
        if (targeted && isPermitted) {
            isPermitted = choiceValidByMatchMax(targeted);
        }
        //end hotspot for both the interacting
        if (choiceKey && isPermitted) {
            isPermitted = choiceValidByMatchMax(getChoiceById(choiceKey));
        }

        if (isPermitted && (selectedChoice || targeted)) {
            isPermitted = isArrowEndChoice(choiceKey);
        }

        return !theSameChoice && !isStartPoint && !isChoicePairedWithSelected && isPermitted;
    }

    /**
     * Test hotspot in inactive state - hotspot reached max associations and
     * @param {Object} choice
     * @returns {Boolean} state
     */
    function isChoiceInactive(choice) {
        let inactive;
        if (cardinality === 'single') {
            inactive = pairs.length === 1 && choiceInPair(choice.key);
        } else {
            inactive = !choiceValidByMatchMax(choice);
        }
        return inactive;
    }

    /**
     * Test hotspot in disabled state - hotspot have not reached max associations but not available for interaction
     * @param {String} choiceKey
     * @returns {Boolean} state
     */
    function isChoiceDisabled(choiceKey) {
        let disabledState = false;
        if (cardinality === 'single') {
            disabledState = pairs.length === 1 && !choiceInPair(choiceKey);
        }
        return disabledState;
    }

    /**
     * Test that choice is permitted for association according to matchMax
     * @param {Object} choice
     * @returns {Boolean} allowed or not
     */
    function choiceValidByMatchMax(choice) {
        // 1 mean one addition association which we are going to create
        const additionalAssociation = 1;
        const choiceUsageCount = getChoiceUsageCountInCompletedPairs(choice.key) + additionalAssociation;
        return choice.matchMax === 0 || choiceUsageCount <= choice.matchMax;
    }

    /**** handlers *****/

    /**
     * Handler for background image load event
     */
    function handleBackgroundImageLoad() {
        imageLoadingPromise.resolve();
    }

    /**
     * Handler for background image load error
     * @param {Error} err
     */
    function handleBackgroundImageError(err) {
        imageLoadingPromise.reject(err);
    }

    /**
     * Adds or removes the actioned choice from the 'selected' list
     * @param {Event} e
     */
    function handleChoiceChange(e) {
        const choiceKey = e?.key;
        const choice = getChoiceById(choiceKey);
        if (isChoiceInactive(choice) || isChoiceDisabled(choice.key)) {
            clearSelectedChoice();
            return;
        }
        if (!selectedChoice) {
            if (!isArrowStartChoice(choiceKey)) {
                return;
            }
            setSelectedChoice(choiceKey);
            //allow focus only non-paired choices
            if (Array.isArray(orderedElementsKeys)) {
                orderedElementsKeys = orderedChoicesKeys.filter(key => {
                    const orderedChoice = getChoiceById(key);
                    return (
                        !isPairExists(key, selectedChoice.key) &&
                        choiceValidByMatchMax(orderedChoice) &&
                        !isChoiceDisabled(key)
                    );
                });
            }
        } else {
            if (
                !isPairExists(selectedChoice.key, choiceKey) &&
                choiceKey !== selectedChoice.key &&
                choiceValidByMatchMax(selectedChoice) &&
                isArrowEndChoice(choiceKey)
            ) {
                const firstKey = useArrow ? selectedChoice.key : choiceKey;
                const secondKey = useArrow ? choiceKey : selectedChoice.key;
                addPair(firstKey, secondKey);
            }
            clearSelectedChoice();
            updateOrderedKeysList();
        }
    }

    /**
     * Measure interaction container's width
     * @param {CustomEvent} event
     */
    function handleInteractionResized(event) {
        const { width } = event.detail;
        if (width) {
            containerWidth = width;
        }
    }

    /**
     * Remove association
     * @param {Event} e
     * @param {Array} pair - pair of choice's keys associated to each other
     */
    function handleRemoveAssociation(e, pair) {
        removePair(pair);
    }

    /**
     * Handle choice keyup
     * @param {KeyboardEvent} e
     * @param {Array} pair - pair of associated choices
     */
    function handleRemoveAssociationByKey(e, pair) {
        const actualKey = getActualKey(e);
        if (actualKey === 'space' || actualKey === 'enter') {
            e.preventDefault();
            const nextKey = getNextOrderedKey(lastFocusedChoiceKey, orderedElementsKeys);
            removePair(pair);
            if (focusChoiceWithKey(nextKey)) {
                lastFocusedChoiceKey = nextKey;
            }
        }
    }

    /**
     * Start process drawing association line
     * Touch on hotspot also starts here
     * @param {String} key - choice key where drawing started
     */
    function startDrawingLine(key) {
        if (!isArrowStartChoice(key)) {
            targeted = null;
            return;
        }
        targeted = getChoiceById(key); //it will be reset on 'finishDrawingLine' or 'handleTouchEnd'
        if (isChoiceInactive(targeted) || isChoiceDisabled(key)) {
            clearState();
            clearSelectedChoice();
            return;
        }
        // if choice is selected it is not a drawing
        if (selectedChoice) {
            return;
        }
        dragging = true;
        end = shapeCenter[key];
        start = shapeCenter[key];
        //actualize coordinates of top-left corner of svg
        const { top, left } = svgElement.getBoundingClientRect();
        svgLeftCoord = left;
        svgTopCoord = top;
    }

    /**
     * Finish drawing association line
     * @param {String} key - choice key where drawing finished
     */
    function finishDrawingLine(key) {
        if (dragging && isLineDrawing && key !== targeted.key) {
            setSelectedChoice(targeted.key); //required for correct call of 'handleChoiceChange'
            handleChoiceChange({ key });
            clearSelectedChoice();
        }
        clearState();
    }

    /**
     * Drawing association line
     * @param {Event} e
     */
    function drawingLine(e) {
        if (dragging) {
            e.preventDefault();
            const { x: mouseX, y: mouseY } = getPointerEventCoords(e);
            // calculate mouse coordinates relative to svg area
            const xCoord = mouseX - svgLeftCoord;
            const yCoord = mouseY - svgTopCoord;
            isLineDrawing = true;
            end = [xCoord, yCoord];
        }
    }

    /**
     * Finish drawing line if mouseup fired out of association hotspot
     * @param {Event} e
     */
    function cancelDrawingLine(e) {
        const element = e.target.closest('.hotspot-choice');
        if ((dragging && isLineDrawing) || (selectedChoice && !element)) {
            clearSelectedChoice();
            clearState();
        }
    }

    /**
     * Clear global variables used for drawing line
     */
    function clearState() {
        dragging = false;
        end = null;
        start = null;
        isLineDrawing = false;
        targeted = null;
    }

    /**
     * Handle stop drawing association line
     * @param {Event} e
     */
    function handleTouchEnd(e) {
        e.preventDefault();
        if (!targeted) {
            return;
        }
        const { x: mouseX, y: mouseY } = getPointerEventCoords(e);
        let key = targeted.key;
        const target = document.elementFromPoint(mouseX, mouseY);
        const choiceEl = target.closest('.hotspot-choice');
        // try to get choice key related with hotspot where touch move was released
        if (choiceEl) {
            key = choiceEl.dataset.choiceKey;
        }
        //if it tap on hotspot - select hotspot
        if (isTap(e.target, target)) {
            //1 condition test select->deselect the same hotspot
            //2 condition test start and end hotspots of drawing line are the same
            if ((selectedChoice && selectedChoice.key === key) || (targeted && targeted.key === key && isLineDrawing)) {
                clearSelectedChoice();
            } else {
                handleChoiceChange({ key });
            }
        }
        finishDrawingLine(key);
    }

    /**
     * Collect centers of each association hotspot
     * @param {Object} e
     */
    function handleCenter(e) {
        const { key, cx, cy } = e;
        shapeCenter[key] = [cx, cy];
    }

    /**
     * Add or remove selected state for association line if line was clicked
     * @param {String} firstChoice
     * @param {String} secondChoice
     */
    function handleLineClick(firstChoice, secondChoice) {
        if (selectedPair) {
            selectedPair = null;
        } else {
            selectedPair = [firstChoice, secondChoice];
            clearSelectedChoice();
        }
    }

    /**
     * Get link on svg element
     * @param {Object} e
     */
    function handleMount(e) {
        svgElement = e.svgElement;
    }

    /**
     * Get focusable choices keys in defined order
     * @param {CustomEvent} e
     */
    function handleChoiceKeyInTabOrder(e) {
        if (!orderedChoicesKeys) {
            orderedChoicesKeys = e.detail;
            updateOrderedKeysList();
        }
    }

    /**
     * Collect association line objects once after draw
     * @param {Object} e
     * @param {Array} pair
     */
    function handleAssociationDraw(e, pair) {
        const key = getPairKey(pair);
        let pairElem = pairElements.find(p => p.key === key);
        if (!pairElem) {
            pairElem = { key, pair };
            pairElements.push(pairElem);
        }
        pairElem.svg = e.svgGroup.findOne('rect.remove-button-hitbox');
        updateOrderedKeysList(); //recalculate list of focusable elements
    }

    /**
     * Reset any unfinished action
     * @param {KeyboardEvent} e
     */
    function resetState(e) {
        const pressedKey = getActualKey(e);
        if (pressedKey === 'esc' && (selectedChoice || targeted)) {
            clearState();
            clearSelectedChoice();
            updateOrderedKeysList(); //recalculate list of focusable elements
            announcement = ariaHelper.announceCancelled();
        }
    }

    /**
     * Handle focusing interaction by keyboard
     * @param {Event} e
     */
    function handleSetFocus(e) {
        hasFocus = e.detail;
        // Cleanup state if focus jump out of interaction
        if (!hasFocus) {
            selectedPair = null;
        }
    }

    /**
     * Flag that focus jumps inside interaction
     */
    function handleInteractionFocusIn() {
        if (!isInteractionFocused) {
            isInteractionFocused = true;
        }
    }
</script>

<style>
    .qti-flow-container {
        &:focus {
            outline: none;
        }
        &:focus-visible {
            @add-mixin simple-outline;
        }
        & .qti-block {
            & :global(.inactive g:hover > .shape-inner-border) {
                stroke: var(--color-brand);
                stroke-width: 0.25rem;
            }
        }
    }
    .glass-layer {
        fill: black;
        opacity: 0.35;
    }
</style>

<svelte:window
    bind:innerHeight={windowHeight}
    onkeydown={resetState}
    onmouseup={cancelDrawingLine}
    ontouchcancel={cancelDrawingLine} />

<div
    class="qti-interaction qti-blockInteraction {qtiClass} {classes}"
    lang={language}
    bind:this={interactionElement}
    onfocusin={handleInteractionFocusIn}
    use:resizeObserve={handleInteractionResized}
    {id}
    {dir}
    {role}
    aria-disabled={disabled || disabledBySession}
    {...ariaAttrs}
    {...dataAttrs}>
    {#if prompt}
        <Prompt blockTree={prompt} />
    {/if}

    <ChoiceFeedbackBlock
        maxChoices={maxAssociations}
        minChoices={minAssociations}
        type="associations"
        qtiMinChoicesMessage={qtiMinAssociationsMessage}
        qtiMaxChoicesMessage={qtiMaxAssociationsMessage}
        {isInteractionFocused}
        {interactionElement}
        selectedNumber={pairs.length}
        lang={instructionsLang} />

    <AtomicAriaLive {announcement} lang={instructionsLang} />

    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <div class="qti-flow-container" tabindex={hasFocus ? '0' : '-1'}>
        <!-- The inner tabstop takes you inside, and the outer tabstop is for getting back out -->
        <div
            class="qti-block"
            tabindex={hasFocus ? '-1' : '0'}
            use:forwardFocusToChoice={{
                choices,
                choiceKeysInTabOrder: orderedElementsKeys,
                lastFocusedChoiceKey,
                focusChoiceWithKey,
                hasFocus,
                isRTL
            }}
            onsetHasFocus={handleSetFocus}
            onsetChoiceKeysTabOrder={handleChoiceKeyInTabOrder}
            onsetLastFocusedChoiceKey={e => (lastFocusedChoiceKey = e.detail)}>
            <Svg
                {itemIdentifier}
                {imgWidth}
                {imgHeight}
                onbackgroundImageLoad={handleBackgroundImageLoad}
                onbackgroundImageError={handleBackgroundImageError}
                onmousemove={handler(drawingLine)}
                ontouchmove={handler(drawingLine)}
                onmount={handleMount}
                {imgSrc}>
                {#snippet renderContent()}
                    <g
                        use:arrowKeysFocusLoop={{
                            focusChoiceWithKey,
                            choiceKeysInTabOrder: orderedElementsKeys,
                            lastFocusedChoiceKey,
                            isRTL
                        }}
                        use:preventSpaceScroll>
                        <g class="hotspots-layer">
                            {#each choices as choice (choice.key)}
                                <HotspotChoice
                                    key={choice.key}
                                    shape={choice.shape}
                                    classes={isChoiceInactive(choice, pairs) ? 'inactive' : ''}
                                    coords={getScaledCoords(choice.coords, scalingFactor)}
                                    selected={choiceInPair(choice.key)}
                                    activated={selectedChoice && selectedChoice.key === choice.key}
                                    disabled={disabled || disabledBySession || isChoiceDisabled(choice.key, pairs)}
                                    checkmark={pairs && choiceInPair(choice.key)}
                                    targeted={choiceIsStartPointOfDrawingLine(choice.key, targeted)}
                                    targetable={!(disabled || disabledBySession) &&
                                        !isChoiceInactive(choice, pairs) &&
                                        (selectedChoice || targeted) &&
                                        associationAllowed(choice.key)}
                                    hoverable={false}
                                    instructions={getChoiceAriaLabel(choice, orderedChoicesKeys, selectedChoice)}
                                    {instructionsLang}
                                    oncenter={handleCenter}
                                    onmousedown={handler(() => startDrawingLine(choice.key))}
                                    ontouchstart={handler(() => startDrawingLine(choice.key))}
                                    onmouseup={handler(() => finishDrawingLine(choice.key))}
                                    ontouchend={handler(handleTouchEnd)}
                                    onmount={e => (choice.svg = e.svgGroup)}
                                    onchange={handleChoiceChange} />
                            {/each}
                        </g>
                        <g class="lines-layer">
                            {#each pairs as pair (getPairKey(pair))}
                                <AssociationLine
                                    disabled={disabled || disabledBySession}
                                    activeLineStart={shapeCenter[pair[0]]}
                                    activeLineEnd={shapeCenter[pair[1]]}
                                    isArrow={useArrow}
                                    arrowStart={getArrowStartPoint(pair)}
                                    arrowEnd={getArrowEndPoint(pair)}
                                    selected={false}
                                    ariaLabel={getRemoveButtonAriaLabel(pair)}
                                    ondraw={e => handleAssociationDraw(e, pair)}
                                    onlineclick={() => handleLineClick(pair[0], pair[1])}
                                    onkeyup={handler(e => handleRemoveAssociationByKey(e, pair))}
                                    onremove={e => handleRemoveAssociation(e, pair)} />
                            {/each}
                            {#if !disabled && isLineDrawing && dragging && start && end}
                                <AssociationLine
                                    activeLineStart={start}
                                    activeLineEnd={end}
                                    isArrow={useArrow}
                                    arrowStart={start}
                                    arrowEnd={end}
                                    disablePointerEvents />
                            {/if}
                        </g>
                        {#if selectedPair !== null}
                            <g class="selection-layer">
                                <!-- svelte-ignore a11y_click_events_have_key_events  -->
                                <!-- svelte-ignore a11y_no_static_element_interactions -->
                                <rect
                                    class="glass-layer"
                                    x="0"
                                    y="0"
                                    height={imgHeight}
                                    width={imgWidth}
                                    onclick={handleLineClick} />
                                {#each getSelectedChoices(selectedPair) as choice (choice.key)}
                                    <HotspotChoice
                                        key={choice.key}
                                        shape={choice.shape}
                                        classes={isChoiceInactive(choice, pairs) ? 'inactive' : ''}
                                        coords={getScaledCoords(choice.coords, scalingFactor)}
                                        selected
                                        disabled={disabled || disabledBySession || isChoiceDisabled(choice.key, pairs)}
                                        checkmark={pairs && choiceInPair(choice.key)}
                                        hoverable={false}
                                        onchange={handleLineClick} />
                                {/each}
                                <AssociationLine
                                    disabled={disabled || disabledBySession}
                                    activeLineStart={shapeCenter[selectedPair[0]]}
                                    activeLineEnd={shapeCenter[selectedPair[1]]}
                                    isArrow={useArrow}
                                    arrowStart={getArrowStartPoint(selectedPair)}
                                    arrowEnd={getArrowEndPoint(selectedPair)}
                                    selected
                                    ariaLabel={getRemoveButtonAriaLabel(selectedPair)}
                                    ondraw={handleDrawShadowLine}
                                    onlineclick={() => handleLineClick(selectedPair[0], selectedPair[1])}
                                    onkeyup={handler(e => handleRemoveAssociationByKey(e, selectedPair))}
                                    onremove={e => handleRemoveAssociation(e, selectedPair)} />
                            </g>
                        {/if}
                    </g>
                {/snippet}
            </Svg>
        </div>
    </div>
</div>
